import { useCallback, useEffect, useRef } from "react";
import { baseWebsocketUrl, websocketProtocols } from "../services/data/axios-base";

const useWebsocketEffect = <T>(
  url: string,
  onOpen: () => void,
  onMessage: (data: any) => void,
  onClose: () => void,
  disabled?: boolean,
  maxRetries?: number,
) => {
  const webSocketRef = useRef<WebSocket | null>(null);
  const webSocketSetup = useRef(false);
  const retryCountRef = useRef(0);

  useEffect(() => {
    if (webSocketRef.current == null && !disabled && retryCountRef.current < (maxRetries ?? 1)) {
      webSocketRef.current = new WebSocket(`${baseWebsocketUrl}/${url}/`, websocketProtocols());
    }

    return () => {
      if (webSocketRef.current != null) {
        webSocketRef.current.close();
      }
      webSocketRef.current = null;
    };
  }, [url, disabled, maxRetries]);

  useEffect(() => {
    if (webSocketRef.current != null && webSocketSetup.current === false) {
      webSocketRef.current.onopen = () => {
        onOpen();
      };

      webSocketRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        onMessage(data.message);
      };

      webSocketRef.current.onclose = () => {
        onClose();
        webSocketRef.current = null;
        webSocketSetup.current = false;
        retryCountRef.current = 0;
      };

      webSocketRef.current.onerror = (error) => {
        console.log('Websocket error:', error);
        if (webSocketRef.current) {
          retryCountRef.current += 1;
          webSocketRef.current.close();
        }
      };

      webSocketSetup.current = true;
    }
  }, [onClose, onMessage, onOpen, webSocketRef]);

  const sendHandler = useCallback(
    (data: T) => {
      if (webSocketRef.current != null && webSocketRef.current?.readyState === WebSocket.OPEN) {
        try {
          webSocketRef.current.send(JSON.stringify(data));
        } catch (error) {
          console.error(error);
        }
      }
    },
    [webSocketRef],
  );

  return sendHandler;
};

export default useWebsocketEffect;
